import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type ServiceStep } from '@/content/services';

/** The node's diameter, and therefore where the spine behind it has to sit. */
const NODE = 36;

/**
 * How a service runs, as a connected track.
 *
 * `NextStepList` draws the same data and is still the right component where
 * it is used — under a form, telling somebody what happens after they press
 * send. This is the service page's version of the same list, and the
 * difference it adds is the spine: a service page's steps are a journey with
 * a length, and seven detached numerals do not say that. A line through them
 * does, before a word is read.
 *
 * Still an `ol` with the numerals drawn from the list's own counter, so a
 * step inserted into the catalogue cannot renumber wrongly, and the spine is
 * a `::before` on the list rather than a segment per item — one element that
 * cannot end up one step short.
 *
 * The spine stops at the last node rather than running to the foot of the
 * list. A line continuing past the final step reads as an unfinished process,
 * which is the opposite of what the last step says.
 */
export function ServiceProcessTrack({ steps }: { steps: readonly ServiceStep[] }) {
  return (
    <Stack
      component="ol"
      spacing={6}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        counterReset: 'service-step',
        position: 'relative',
        // The spine, behind the nodes. Inset by half a node so it runs
        // through their centres, and stopped short of the last one by that
        // node's own height plus the gap it does not need to cross.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: NODE / 2 - 1,
          top: NODE / 2,
          bottom: NODE / 2,
          width: 2,
          borderRadius: 1,
          bgcolor: 'divider',
          // Decoration only; it carries nothing the list does not already say.
          pointerEvents: 'none',
        },
      }}
    >
      {steps.map((step) => (
        <Stack
          key={step.title}
          component="li"
          direction="row"
          spacing={4}
          sx={{ counterIncrement: 'service-step', position: 'relative' }}
        >
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: NODE,
              height: NODE,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontSize: '0.8125rem',
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: 'background.paper',
              border: 2,
              borderColor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.38)',
              // Paper-filled and bordered rather than tinted, so the spine
              // behind it is covered: a translucent node would show the line
              // running straight through the numeral.
              '&::before': { content: 'counter(service-step)' },
            }}
          />
          <Stack spacing={1.5} sx={{ pt: 0.75, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step.text}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
